import { useEffect, useState } from 'react';
import { Eye, Calendar, BarChart3, Users, Mail, MessageSquare, Phone, MapPin, Clock, CheckCircle, X, Search, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/AdminLayout';

interface AnalyticsData {
  totalViews: number;
  viewsToday: number;
  viewsThisWeek: number;
  viewsThisMonth: number;
  dailyViews: { date: string; count: number }[];
  monthlyViews: { month: string; count: number }[];
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  created_at: string;
}

interface QuoteRequest {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  city: string;
  message?: string;
  car_model_name?: string;
  car_brand_name?: string;
  status: 'pending' | 'processed' | 'rejected';
  created_at: string;
}

export function Analytics() {
  const [data, setData] = useState<AnalyticsData>({
    totalViews: 0,
    viewsToday: 0,
    viewsThisWeek: 0,
    viewsThisMonth: 0,
    dailyViews: [],
    monthlyViews: []
  });
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7j'); // 7j, 30j, 90j, 1a
  const [activeTab, setActiveTab] = useState<'contacts' | 'quotes'>('contacts');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAnalytics();
    loadContacts();
    loadQuoteRequests();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      let rangeStart;
      if (timeRange === '7j') rangeStart = weekStart;
      else if (timeRange === '30j') rangeStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      else if (timeRange === '90j') rangeStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      else rangeStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

      const [
        viewsResult, 
        todayViewsResult, 
        weekViewsResult, 
        monthViewsResult,
        dailyViewsResult,
        monthlyViewsResult
      ] = await Promise.all([
        supabase.from('page_views').select('id', { count: 'exact', head: true }),
        supabase.from('page_views').select('id', { count: 'exact', head: true }).gte('viewed_at', todayStart.toISOString()),
        supabase.from('page_views').select('id', { count: 'exact', head: true }).gte('viewed_at', weekStart.toISOString()),
        supabase.from('page_views').select('id', { count: 'exact', head: true }).gte('viewed_at', monthStart.toISOString()),
        supabase.rpc('get_daily_views', { days: timeRange === '7j' ? 7 : timeRange === '30j' ? 30 : timeRange === '90j' ? 90 : 365 }),
        supabase.rpc('get_monthly_views', {})
      ]);

      const days = timeRange === '7j' ? 7 : timeRange === '30j' ? 30 : timeRange === '90j' ? 90 : 365;
      const lastDays = Array.from({ length: days }, (_, i) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (days - 1 - i));
        return date.toISOString().split('T')[0];
      });

      const dailyViewsData = lastDays.map(date => {
        const count = (dailyViewsResult.data || []).find((d: any) => d.date === date)?.count || 0;
        return { date, count };
      });

      setData({
        totalViews: viewsResult.count || 0,
        viewsToday: todayViewsResult.count || 0,
        viewsThisWeek: weekViewsResult.count || 0,
        viewsThisMonth: monthViewsResult.count || 0,
        dailyViews: dailyViewsData,
        monthlyViews: monthlyViewsResult.data || []
      });
    } catch (error) {
      console.error('Erreur lors du chargement des analyses:', error);
    }
  };

  const loadContacts = async () => {
    try {
      // Assuming there's a contact_submissions table
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading contacts:', error);
        // If the table doesn't exist, we'll just use an empty array
        setContacts([]);
      } else {
        setContacts(data || []);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    }
  };

  const loadQuoteRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading quote requests:', error);
      } else {
        setQuoteRequests(data || []);
      }
    } catch (error) {
      console.error('Error loading quote requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format number with thousand separator
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-TN').format(num);
  };

  // Custom Area Chart Component
  const AreaChart = ({ data }: { data: { date: string; count: number }[] }) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const height = 200;
    const width = 100;
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (d.count / maxCount) * height * 0.8 - 10;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="relative h-48 w-full">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="5%" stopColor="#D4452C" stopOpacity="0.8"/>
              <stop offset="95%" stopColor="#D4452C" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
          <polygon
            points={`0,${height} ${points} ${width},${height}`}
            fill="url(#gradient)"
          />
          <polyline
            points={points}
            fill="none"
            stroke="#D4452C"
            strokeWidth="2"
          />
          {data.map((d, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - (d.count / maxCount) * height * 0.8 - 10;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="3"
                fill="#D4452C"
                className="hover:r-4 transition-all cursor-pointer"
              />
            );
          })}
        </svg>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {data.filter((_, i) => i % Math.ceil(data.length / 7) === 0).map((d, i) => (
            <span key={i}>{formatDate(d.date)}</span>
          ))}
        </div>
      </div>
    );
  };

  // Custom Line Chart Component
  const LineChart = ({ data }: { data: { month: string; count: number }[] }) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const height = 250;
    const width = 100;
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (d.count / maxCount) * height * 0.8 - 20;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="relative h-64 w-full">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
          <polyline
            points={points}
            fill="none"
            stroke="#D4452C"
            strokeWidth="2"
          />
          {data.map((d, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - (d.count / maxCount) * height * 0.8 - 20;
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r="4"
                  fill="#D4452C"
                  className="hover:r-5 transition-all cursor-pointer"
                />
                <text
                  x={x}
                  y={y - 10}
                  textAnchor="middle"
                  className="text-xs fill-gray-600"
                >
                  {d.count}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {data.map((d, i) => (
            <span key={i} className="text-center">
              {new Date(d.month).toLocaleDateString('fr-TN', { month: 'short' })}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const filteredContacts = contacts.filter(contact => 
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredQuoteRequests = quoteRequests.filter(request => 
    request.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (request.car_model_name && request.car_model_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (request.car_brand_name && request.car_brand_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <AdminLayout currentPage="analytics">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Tableau de Bord Analytique</h2>
          <p className="text-sm text-gray-600">Statistiques des visiteurs et demandes de contact</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setTimeRange('7j')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${timeRange === '7j' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            7 jours
          </button>
          <button
            onClick={() => setTimeRange('30j')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${timeRange === '30j' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            30 jours
          </button>
          <button
            onClick={() => setTimeRange('90j')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${timeRange === '90j' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            90 jours
          </button>
          <button
            onClick={() => setTimeRange('1a')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${timeRange === '1a' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            1 an
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg p-4 border border-gray-200 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Viewer Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-blue-100 p-2 rounded-md">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  +{Math.round((data.viewsToday / Math.max(data.viewsThisWeek - data.viewsToday, 1)) * 100)}%
                </span>
              </div>
              <p className="text-gray-600 text-xs font-medium mb-1">Vues Totales</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(data.totalViews)}</p>
              <p className="text-gray-500 text-xs mt-1">
                {formatNumber(data.viewsToday)} aujourd'hui
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-green-100 p-2 rounded-md">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  Semaine
                </span>
              </div>
              <p className="text-gray-600 text-xs font-medium mb-1">Vues Cette Semaine</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(data.viewsThisWeek)}</p>
              <p className="text-gray-500 text-xs mt-1">
                {formatNumber(data.viewsThisMonth)} ce mois
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-yellow-100 p-2 rounded-md">
                  <Users className="h-5 w-5 text-yellow-600" />
                </div>
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {contacts.length}
                </span>
              </div>
              <p className="text-gray-600 text-xs font-medium mb-1">Contacts Reçus</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(contacts.length)}</p>
              <p className="text-gray-500 text-xs mt-1">
                {quoteRequests.length} demandes de devis
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="bg-purple-100 p-2 rounded-md">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                  Moyenne
                </span>
              </div>
              <p className="text-gray-600 text-xs font-medium mb-1">Vues/Jour (7j)</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(Math.round(data.viewsThisWeek / 7))}</p>
              <p className="text-gray-500 text-xs mt-1">
                Moyenne quotidienne
              </p>
            </div>
          </div>

          {/* Viewer Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Trafic des Visiteurs</h3>
                <BarChart3 className="h-5 w-5 text-[#D4452C]" />
              </div>
              <AreaChart data={data.dailyViews} />
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Tendances Mensuelles</h3>
                <Calendar className="h-5 w-5 text-[#D4452C]" />
              </div>
              <LineChart data={data.monthlyViews} />
            </div>
          </div>

          {/* Contacts and Quote Requests */}
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Contacts et Demandes de Devis</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${activeTab === 'contacts' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  <Mail className="inline h-4 w-4 mr-1" />
                  Contacts
                </button>
                <button
                  onClick={() => setActiveTab('quotes')}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${activeTab === 'quotes' ? 'bg-[#D4452C] text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  <MessageSquare className="inline h-4 w-4 mr-1" />
                  Devis
                </button>
              </div>
            </div>

            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={`Rechercher ${activeTab === 'contacts' ? 'un contact' : 'une demande de devis'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm"
                />
              </div>
            </div>

            {activeTab === 'contacts' ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nom
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sujet
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredContacts.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                          {searchTerm ? 'Aucun contact trouvé' : 'Aucun contact reçu'}
                        </td>
                      </tr>
                    ) : (
                      filteredContacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contact.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contact.subject}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(contact.created_at)}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Modèle
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuoteRequests.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          {searchTerm ? 'Aucune demande de devis trouvée' : 'Aucune demande de devis reçue'}
                        </td>
                      </tr>
                    ) : (
                      filteredQuoteRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {request.first_name} {request.last_name}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {request.city}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 flex items-center mb-1">
                              <Mail className="h-3 w-3 mr-1 text-gray-400" />
                              <span className="truncate max-w-[200px]">{request.email}</span>
                            </div>
                            <div className="text-sm text-gray-900 flex items-center">
                              <Phone className="h-3 w-3 mr-1 text-gray-400" />
                              {request.phone}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {request.car_brand_name && request.car_model_name ? (
                                <div>
                                  <div className="font-medium">{request.car_brand_name}</div>
                                  <div className="text-xs text-gray-500">{request.car_model_name}</div>
                                </div>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {request.status === 'pending' && <Clock className="h-4 w-4 text-orange-600 mr-2" />}
                              {request.status === 'processed' && <CheckCircle className="h-4 w-4 text-green-600 mr-2" />}
                              {request.status === 'rejected' && <X className="h-4 w-4 text-red-600 mr-2" />}
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                request.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                                request.status === 'processed' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {request.status === 'pending' ? 'En attente' :
                                 request.status === 'processed' ? 'Traité' : 'Rejeté'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(request.created_at)}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
}